import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import BottomSheet from '../../../../components/ui/BottomSheet';
import { fmtPrice, cn } from '../../../../lib/utils';
import { riskUnitLabel, formatRiskWithUnit } from '../riskUnits';
import { qtyColumnLabel, qtyRowField, formatQtyWithMode } from '../qtyModes';
import { friendlyTradeIn, friendlyProductType, friendlyQtyMode, formatLeg, manualOrderTypeLabel } from '../manualLabels';
import { normalizeMode } from '../utils/manualSignalResult';
import { useLtpStore } from '../../../../store/ltpStore';

// Mobile-only full-width mode banner (lg:hidden) — mirrors the M2C2 order-entry
// sheet banner's copy/tokens so the safety cue is consistent across both sheets.
// Desktop keeps the existing small execution-mode badge in the header, unchanged.
function ModeBanner({ isLive }) {
  const cls = isLive ? 'bg-bear/10 border-bear/30 text-bear' : 'bg-info/10 border-info/30 text-info';
  const title = isLive ? 'LIVE MODE' : 'PAPER MODE';
  const subtext = isLive ? 'Real broker order can be placed' : 'No real broker order will be placed';
  return (
    <div className={cn('lg:hidden rounded-lg border px-3 py-2.5 mb-3', cls)}>
      <div className="flex items-center gap-1.5">
        <span className="mt-dot w-1.5 h-1.5 rounded-full bg-current shrink-0" />
        <span className="text-xs font-semibold">{title}</span>
      </div>
      <p className="text-2xs mt-0.5">{subtext}</p>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div className="flex items-center justify-between py-1.5 border-b border-bg-border/50 last:border-0">
      <span className="text-2xs font-mono uppercase tracking-widest text-txt-label">{label}</span>
      <span className="text-xs font-mono text-txt-primary">{value}</span>
    </div>
  );
}

const decPlaces = (s) => { const str = String(s ?? ''); const i = str.indexOf('.'); return i < 0 ? 0 : str.length - i - 1; };
// Tick-multiple check via integer scaling — avoids binary-float modulo errors (e.g. 7165.05 % 0.05).
function isTickMultiple(priceStr, tickStr) {
  const p = Number(priceStr); const t = Number(tickStr);
  if (!Number.isFinite(p) || !Number.isFinite(t) || p <= 0 || t <= 0) return false;
  const scale = Math.pow(10, Math.max(decPlaces(priceStr), decPlaces(tickStr)));
  const pi = Math.round(p * scale); const ti = Math.round(t * scale);
  return ti > 0 && pi % ti === 0;
}

// side: 'BUY' | 'SELL'. ctx: { strategy, symbol, mode, prepared? }.
// PAPER/LIVE: ctx.prepared holds the /prepare result. Trigger Price is editable (tick-validated),
// Quantity is read-only, LTP updates live from the store, and a backend-driven countdown gates Confirm.
export default function ConfirmManualSignalModal({ open, side, ctx, submitting = false, error = null, onConfirm, onCancel, onExpire, canUseFanout = false }) {
  const prepared = ctx?.prepared || null;
  const isPrepared = !!prepared;
  const isLive = String(prepared?.mode || ctx?.mode || '').toUpperCase() === 'LIVE';
  // Trigger Price inits from the prepared frozen value.
  const [trigger, setTrigger] = useState(() => (ctx?.prepared?.trigger_price ?? ''));
  const [nowMs, setNowMs] = useState(() => Date.now());
  const liveLtpMap = useLtpStore((s) => s.ltp);

  const expiresMs = prepared ? Date.parse(prepared.expires_at) : null;
  useEffect(() => {
    setTrigger(ctx?.prepared?.trigger_price ?? '');
    setNowMs(Date.now());
  }, [ctx?.prepared?.intent_id, ctx?.prepared?.trigger_price]);
  // Countdown ticks for prepared orders. Cleared on unmount/close → no timer leaks.
  useEffect(() => {
    if (!isPrepared) return undefined;
    const id = setInterval(() => setNowMs(Date.now()), 250);
    return () => clearInterval(id);
  }, [isPrepared]);

  const remainingSec = expiresMs != null ? Math.max(0, Math.ceil((expiresMs - nowMs) / 1000)) : null;
  const expired = isPrepared && remainingSec === 0;
  // On expiry, hand off to the parent (it clears prepared state BEFORE closing so a late click can't confirm).
  useEffect(() => {
    if (isPrepared && expiresMs != null && nowMs >= expiresMs) onExpire?.();
  }, [isPrepared, nowMs, expiresMs, onExpire]);

  if (!open || !ctx) return null;
  const { strategy, symbol } = ctx;
  const executionMode = normalizeMode(strategy?.executionMode) || 'PAPER';
  const mt = strategy.manual_trading || {};
  const isBuy = side === 'BUY';
  const isOption = String(mt.trade_in || '').toUpperCase() === 'OPTION';

  const qtyMode = mt.qty_mode;
  const qtyVal = symbol[qtyRowField(qtyMode)];
  const ru = symbol.riskUnit || strategy.riskUnit;
  const legs = isOption ? (isBuy ? strategy.execution?.bullish_signal_legs : strategy.execution?.bearish_signal_legs) : null;
  const mappingLabel = isBuy ? 'Bullish Option Mapping' : 'Bearish Option Mapping';

  // ── Prepared values ──
  const tickValid = isPrepared ? isTickMultiple(trigger, prepared.resolved_tick_size) : true;
  const normSym = String(prepared?.canonical_symbol || symbol.symbol || '').trim().toUpperCase();
  const liveLtp = isPrepared ? (liveLtpMap[normSym] ?? prepared.ltp) : symbol.ltp;
  const marginEdited = isPrepared && prepared.quantity_mode === 'BY_MARGIN' && String(trigger) !== String(prepared.trigger_price);
  const preparedTradeIn = String(prepared?.trade_in || mt.trade_in || '').toUpperCase();
  const preparedIsOption = preparedTradeIn === 'OPTION' && !!prepared?.resolved_option_symbol;

  const confirmDisabled = submitting || (isPrepared && (expired || !tickValid));
  const handleConfirm = () => { if (confirmDisabled) return; onConfirm(trigger); };

  const body = (
    <>
      <div className="hidden lg:flex items-center justify-between mb-4">
        <h3 className="font-display font-semibold text-txt-primary">
          Confirm Manual <span className={isBuy ? 'text-bull' : 'text-bear'}>{side}</span>
        </h3>
        <span className={cn('badge border text-2xs', isLive ? 'bg-bear/10 border-bear/30 text-bear' : 'bg-info/10 border-info/30 text-info')}>
          {executionMode}
        </span>
      </div>

      <ModeBanner isLive={isLive} />

      {isPrepared ? (
          /* ── Prepared order ── */
          <div className="mb-4">
            {preparedIsOption ? (
              <>
                <Field label="Base Symbol" value={prepared.base_symbol || prepared.source_symbol || symbol.displaySymbol || symbol.symbol} />
                <Field label="Base LTP" value={fmtPrice(prepared.base_ltp)} />
                <Field label="Mapping Used" value={prepared.mapping_direction === 'BEARISH' ? 'Bearish Option Mapping' : 'Bullish Option Mapping'} />
                <Field label="Resolved Option" value={prepared.resolved_option_symbol || prepared.canonical_symbol} />
                <Field label="Option LTP" value={fmtPrice(prepared.option_ltp || prepared.ltp)} />
                <Field label="Strike" value={prepared.strike || '—'} />
                <Field label="Expiry" value={prepared.expiry || '—'} />
                <Field label="CE/PE" value={prepared.option_type || '—'} />
                <Field label="Leg Action" value={prepared.leg_action || side} />
              </>
            ) : (
              <>
                <Field label="Symbol" value={symbol.displaySymbol || prepared.canonical_symbol} />
                <Field label="LTP" value={fmtPrice(liveLtp)} />
              </>
            )}
            <Field label="Side" value={side} />
            <Field label="MPP" value={`${prepared.market_protection_pct}%`} />
            <div className="flex items-center justify-between py-1.5 border-b border-bg-border/50">
              <span className="text-2xs font-mono uppercase tracking-widest text-txt-label">Trigger Price</span>
              <input
                type="number"
                inputMode="decimal"
                value={trigger}
                onChange={(e) => setTrigger(e.target.value)}
                className={cn('input-field !w-32 text-right text-base !h-11 lg:!h-7 lg:text-xs', !tickValid && 'border-bear/60')}
              />
            </div>
            {!tickValid && (
              <p className="text-2xs font-mono text-bear text-right pt-1">Must be a positive multiple of tick {prepared.resolved_tick_size}</p>
            )}
            <Field
              label={prepared.quantity_mode === 'BY_MARGIN' ? 'Quantity (Margin)' : 'Quantity'}
              value={marginEdited ? `${prepared.quantity} · recalculated on Confirm` : prepared.quantity}
            />
            <Field label="Order Type" value={manualOrderTypeLabel(prepared.effective_order_type)} />
            <Field
              label="Confirm In"
              value={<span className={cn('font-medium', expired ? 'text-bear' : remainingSec <= 10 ? 'text-warn' : 'text-txt-primary')}>{remainingSec}s</span>}
            />
          </div>
        ) : (
          /* ── PAPER (unchanged) ── */
          <div className="mb-4">
            <Field label="Strategy" value={strategy.strategyName} />
            <Field label="Symbol" value={symbol.displaySymbol || symbol.symbol} />
            <Field label="Current LTP" value={fmtPrice(symbol.ltp)} />
            <div className="flex items-center justify-between py-1.5 border-b border-bg-border/50">
              <span className="text-2xs font-mono uppercase tracking-widest text-txt-label">Trigger Price</span>
              <input
                type="number"
                inputMode="decimal"
                value={trigger}
                onChange={(e) => setTrigger(e.target.value)}
                placeholder={fmtPrice(symbol.ltp)}
                className="input-field !w-28 text-right text-base !h-11 lg:!h-7 lg:text-xs"
              />
            </div>
            <Field label="Trade In" value={friendlyTradeIn(mt.trade_in)} />
            <Field label="Product Type" value={friendlyProductType(mt.product_type)} />
            <Field label="Qty Mode" value={friendlyQtyMode(qtyMode)} />
            <Field label={qtyColumnLabel(qtyMode, mt.segment)} value={formatQtyWithMode(qtyVal, qtyMode)} />
            <Field label="Order Type" value={manualOrderTypeLabel(symbol.orderType || 'Market')} />
            <Field label="MPP" value={`${symbol.mpp == null || symbol.mpp === '' ? '0' : symbol.mpp}%`} />
            <Field label="Risk Unit" value={riskUnitLabel(ru)} />
            <Field label="Target" value={formatRiskWithUnit(symbol.target, ru)} />
            <Field label="Stop Loss" value={formatRiskWithUnit(symbol.stopLoss, ru)} />
          </div>
        )}

        {canUseFanout && (
          <div className="rounded-md border border-amber/25 bg-amber/5 px-3 py-3 mb-4 space-y-1">
            <p className="text-2xs font-mono text-txt-secondary">
              This signal will be copied to active mapped accounts.
            </p>
            <p className="text-2xs font-mono text-txt-secondary">
              Final execution price and P&amp;L may vary due to market movement.
            </p>
            <p className="text-2xs font-mono text-txt-muted">
              Exit Signal will be generated based on the master signal.
            </p>
          </div>
        )}

        {/* Legacy unprepared PAPER preview fallback. New PAPER/LIVE orders use prepared canonical symbols. */}
        {!isPrepared && (isOption ? (
          <div className="rounded-md border border-amber/25 bg-amber/5 px-3 py-2.5 mb-5 space-y-1">
            <p className="text-2xs font-mono">
              <span className="text-txt-label">Mapping Used: </span>
              <span className="text-amber">{mappingLabel}</span>
            </p>
            <p className="text-2xs font-mono">
              <span className="text-txt-label">Leg Preview: </span>
              <span className="text-txt-secondary">{legs?.length ? legs.map(formatLeg).join('  ·  ') : '—'}</span>
            </p>
            <p className="text-2xs font-mono">
              <span className="text-txt-label">Resolved Option: </span>
              <span className="text-txt-muted">Not connected yet</span>
            </p>
          </div>
        ) : (
          <div className="rounded-md border border-info/25 bg-info/5 px-3 py-2.5 mb-5 space-y-1">
            <p className="text-2xs font-mono">
              <span className="text-txt-label">Executable Symbol: </span>
              <span className="text-txt-secondary">{symbol.symbol || symbol.displaySymbol}</span>
            </p>
            <p className="text-2xs font-mono text-txt-muted">
              Underlying strategy — trades the selected symbol directly.
            </p>
          </div>
        ))}

        {error && (
          <div className="rounded-md border border-bear/40 bg-bear/10 px-3 py-2.5 mb-4">
            <p className="text-xs font-mono text-bear break-words">{error}</p>
          </div>
        )}

      {/* Mobile (<lg): sticky footer within the BottomSheet's scrollable body so Confirm
          stays reachable while the countdown runs (same idiom as the order-entry sheet).
          lg: resets restore the exact previous desktop modal presentation. */}
      <div className="sticky bottom-0 -mx-4 px-4 pt-3 pb-2 bg-bg-card border-t border-bg-border flex justify-end gap-3 lg:static lg:mx-0 lg:px-0 lg:pt-0 lg:pb-0 lg:bg-transparent lg:border-0">
        <button
          type="button"
          onClick={onCancel}
          disabled={submitting}
          className="btn-ghost disabled:opacity-50 min-h-[48px] lg:min-h-0"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleConfirm}
          disabled={confirmDisabled}
          className={cn(
            'font-semibold text-sm px-4 py-2 rounded-md border transition-colors disabled:opacity-60 disabled:cursor-not-allowed min-h-[48px] lg:min-h-0',
            isBuy
              ? 'bg-bull border-bull text-white hover:bg-bull/90 active:bg-bull/80 lg:bg-bull/15 lg:border-bull/40 lg:text-bull lg:hover:bg-bull/25'
              : 'bg-bear border-bear text-white hover:bg-bear/90 active:bg-bear/80 lg:bg-bear/15 lg:border-bear/40 lg:text-bear lg:hover:bg-bear/25',
          )}
        >
          {submitting ? `Submitting ${executionMode} order…` : `Confirm ${side}`}
        </button>
      </div>
    </>
  );

  return createPortal(
    <>
      {/* Desktop (>=lg): unchanged centered modal */}
      <div className="hidden lg:flex fixed inset-0 z-[1100] items-center justify-center bg-black/60 backdrop-blur-sm p-4">
        <div className="card p-6 w-full max-w-md max-h-[90vh] overflow-y-auto animate-fade-in shadow-panel">
          {body}
        </div>
      </div>

      {/* Mobile (<lg): bottom-sheet presentation of the same content. The BottomSheet
          title row owns the visible mobile heading so the close X aligns with it. */}
      <BottomSheet
        open
        onClose={onCancel}
        title={(
          <span className="font-display font-semibold text-txt-primary">
            Confirm Manual <span className={isBuy ? 'text-bull' : 'text-bear'}>{side}</span>
          </span>
        )}
      >
        {body}
      </BottomSheet>
    </>,
    document.body,
  );
}
